import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowLeft, Save, User, Mail, Lock, Shield, Building2, Calendar, X, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { UsuariosService, DepartamentosService, UnidadesService } from '../../services';
import { Departamento, Unidade } from '../../types';

interface FormData {
  nome: string;
  email: string;
  password: string;
  password_confirmation: string;
  nivel: 'usuario' | 'admin' | 'dev';
  departamentos: number[];
  unidades: number[];
  status: number;
  exibir_fechados: number;
}

const NewUser: React.FC = () => {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const [departments, setDepartments] = useState<Departamento[]>([]);
  const [units, setUnits] = useState<Unidade[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    nome: '',
    email: '',
    password: '',
    password_confirmation: '',
    nivel: 'usuario',
    departamentos: [],
    unidades: [],
    status: 1,
    exibir_fechados: 1
  });

  // Verificar se o usuário tem acesso
  useEffect(() => {
    if (usuario && usuario.nivel !== 'dev') {
      navigate('/dashboard');
      toast.error('Acesso negado. Apenas desenvolvedores podem acessar esta página.');
      return;
    }

    if (usuario) {
      loadDepartments();
      loadUnits();
    }
  }, [usuario, navigate]);

  const loadDepartments = async () => {
    try {
      const response = await DepartamentosService.getDepartamentos();
      setDepartments(response || []);
    } catch (error) {
      console.error('Erro ao carregar departamentos:', error);
      toast.error('Erro ao carregar departamentos');
    }
  };

  const loadUnits = async () => {
    try {
      const response = await UnidadesService.getUnidades();
      setUnits(response || []);
    } catch (error) {
      console.error('Erro ao carregar unidades:', error);
      toast.error('Erro ao carregar unidades');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'status' || name === 'exibir_fechados' ? parseInt(value) : value
    }));

    // Limpar seleções quando o nível mudar
    if (name === 'nivel') {
      setFormData(prev => ({
        ...prev,
        nivel: value as 'usuario' | 'admin' | 'dev',
        departamentos: [],
        unidades: []
      }));
    }
  };

  const handleDepartamentoChange = (departamentoId: number) => {
    setFormData(prev => ({
      ...prev,
      departamentos: prev.departamentos.includes(departamentoId)
        ? prev.departamentos.filter(id => id !== departamentoId)
        : [...prev.departamentos, departamentoId]
    }));
  };

  const handleUnidadeChange = (unidadeId: number) => {
    setFormData(prev => ({
      ...prev,
      unidades: prev.unidades.includes(unidadeId)
        ? prev.unidades.filter(id => id !== unidadeId)
        : [...prev.unidades, unidadeId]
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.nome.trim()) {
      toast.error('Nome é obrigatório');
      return false;
    }
    if (!formData.email.trim()) {
      toast.error('Email é obrigatório');
      return false;
    }
    if (!formData.password) {
      toast.error('Senha é obrigatória');
      return false;
    }
    if (formData.password.length < 6) {
      toast.error('Senha deve ter pelo menos 6 caracteres');
      return false;
    }
    if (formData.password !== formData.password_confirmation) {
      toast.error('Senhas não coincidem');
      return false;
    }

    // Validação específica por nível
    if (formData.nivel === 'admin' || formData.nivel === 'dev') {
      if (formData.departamentos.length === 0) {
        toast.error('Administradores e desenvolvedores devem ter pelo menos um departamento');
        return false;
      }
      if (formData.unidades.length > 0) {
        toast.error('Administradores e desenvolvedores não podem ter unidades');
        return false;
      }
    } else {
      if (formData.unidades.length === 0) {
        toast.error('Usuários devem ter pelo menos uma unidade');
        return false;
      }
      if (formData.departamentos.length > 0) {
        toast.error('Usuários não podem ter departamentos');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const userData = {
        nome: formData.nome.trim(),
        email: formData.email.trim(),
        password: formData.password,
        nivel: formData.nivel,
        status: formData.status,
        exibir_fechados: formData.exibir_fechados
      };

      // Adicionar relacionamentos baseado no nível
      if (formData.nivel === 'admin' || formData.nivel === 'dev') {
        (userData as any).departamentos = formData.departamentos;
      } else {
        (userData as any).unidades = formData.unidades;
      }

      await UsuariosService.createUsuario(userData);
      toast.success('Usuário criado com sucesso!');
      navigate('/usuarios');
    } catch (error: any) {
      console.error('Erro ao criar usuário:', error);
      if (error.message?.includes('Acesso negado')) {
        toast.error('Você não tem permissão para criar usuários');
        navigate('/dashboard');
      } else {
        toast.error('Erro ao criar usuário. Tente novamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Se não for dev, não renderizar nada
  if (!usuario || usuario.nivel !== 'dev') {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/usuarios')}
            className="btn btn-outline btn-sm p-2 hover:bg-gray-50 dark:hover:bg-gray-700/50"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-3">
              <User className="text-primary-600 dark:text-primary-400" size={28} />
              Novo Usuário
            </h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
              Crie um novo usuário no sistema
            </p>
          </div>
        </div>
      </div>

      {/* Formulário */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informações Básicas */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <User size={20} className="text-primary-600 dark:text-primary-400" />
              Informações Básicas
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="nome" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  id="nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                  placeholder="Digite o nome completo"
                  required
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                  placeholder="usuario@exemplo.com"
                  required
                />
              </div>
            </div>
          </div>

          {/* Senha */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Lock size={20} className="text-primary-600 dark:text-primary-400" />
              Senha
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Senha *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 pr-10 border-2 border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                    placeholder="Mínimo 6 caracteres"
                    minLength={6}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="password_confirmation" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Confirmar Senha *
                </label>
                <div className="relative">
                  <input
                    type={showPasswordConfirmation ? "text" : "password"}
                    id="password_confirmation"
                    name="password_confirmation"
                    value={formData.password_confirmation}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 pr-10 border-2 border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                    placeholder="Confirme a senha"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
                  >
                    {showPasswordConfirmation ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Permissões e Configurações */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Shield size={20} className="text-primary-600 dark:text-primary-400" />
              Permissões e Configurações
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="nivel" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nível de Acesso *
                </label>
                <select
                  id="nivel"
                  name="nivel"
                  value={formData.nivel}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  required
                >
                  <option value="usuario">Gerente</option>
                  <option value="admin">Operador</option>
                  <option value="dev">Programador</option>
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {formData.nivel === 'usuario' 
                    ? 'Usuários podem ter múltiplas unidades'
                    : 'Administradores e desenvolvedores podem ter múltiplos departamentos'
                  }
                </p>
              </div>

              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value={1}>Ativo</option>
                  <option value={0}>Inativo</option>
                </select>
              </div>
            </div>
          </div>

          {/* Organizacional */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Building2 size={20} className="text-primary-600 dark:text-primary-400" />
              Organizacional
            </h3>
            
            {/* Departamentos (apenas para admin e dev) */}
            {(formData.nivel === 'admin' || formData.nivel === 'dev') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Departamentos *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-48 overflow-y-auto border-2 border-gray-200 dark:border-gray-600 rounded-lg p-3 bg-white dark:bg-gray-700">
                  {departments.map(dept => (
                    <label key={dept.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 p-2 rounded">
                      <input
                        type="checkbox"
                        checked={formData.departamentos.includes(dept.id)}
                        onChange={() => handleDepartamentoChange(dept.id)}
                        className="w-4 h-4 text-primary-600 dark:text-primary-400 bg-gray-100 dark:bg-gray-600 border-gray-300 dark:border-gray-500 rounded focus:ring-primary-500 dark:focus:ring-primary-400 focus:ring-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{dept.nome}</span>
                    </label>
                  ))}
                </div>
                {formData.departamentos.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {formData.departamentos.map(deptId => {
                      const dept = departments.find(d => d.id === deptId);
                      return (
                        <span key={deptId} className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-400 text-xs rounded-full">
                          {dept?.nome}
                          <button
                            type="button"
                            onClick={() => handleDepartamentoChange(deptId)}
                            className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300"
                          >
                            <X size={12} />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Unidades (apenas para usuários) */}
            {formData.nivel === 'usuario' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Unidades *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-48 overflow-y-auto border-2 border-gray-200 dark:border-gray-600 rounded-lg p-3 bg-white dark:bg-gray-700">
                  {units.map(unit => (
                    <label key={unit.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 p-2 rounded">
                      <input
                        type="checkbox"
                        checked={formData.unidades.includes(unit.id)}
                        onChange={() => handleUnidadeChange(unit.id)}
                        className="w-4 h-4 text-primary-600 dark:text-primary-400 bg-gray-100 dark:bg-gray-600 border-gray-300 dark:border-gray-500 rounded focus:ring-primary-500 dark:focus:ring-primary-400 focus:ring-2"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{unit.nome}</span>
                    </label>
                  ))}
                </div>
                {formData.unidades.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {formData.unidades.map(unitId => {
                      const unit = units.find(u => u.id === unitId);
                      return (
                        <span key={unitId} className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900/50 text-primary-800 dark:text-primary-400 text-xs rounded-full">
                          {unit?.nome}
                          <button
                            type="button"
                            onClick={() => handleUnidadeChange(unitId)}
                            className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300"
                          >
                            <X size={12} />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Configurações Adicionais */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Calendar size={20} className="text-primary-600 dark:text-primary-400" />
              Configurações Adicionais
            </h3>
            
            <div>
              <label htmlFor="exibir_fechados" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Exibir Tickets Fechados
              </label>
              <select
                id="exibir_fechados"
                name="exibir_fechados"
                value={formData.exibir_fechados}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border-2 border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value={1}>Sim</option>
                <option value={0}>Não</option>
              </select>
            </div>
          </div>

          {/* Botões */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => navigate('/usuarios')}
              className="btn btn-outline flex-1 sm:flex-none"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-primary flex-1 sm:flex-none flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Criando...
                </>
              ) : (
                <>
                  <Save size={20} />
                  Criar Usuário
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewUser;
